import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'
import {useShouldShowHotelCardClickersContent} from 'components/Accommodation/AccommodationView/Desktop/useShouldShowHotelCardClickersContent'
import {HotelLocationContent} from 'components/Accommodation/HotelLocationContent'
import {DesktopAPClickerCTA} from 'components/Accommodation/Offers/DesktopAPClickerCTA'
import {PrimaryRoomOffer} from 'components/Accommodation/Offers/PrimaryRoomOffer'
import SpinnerImportLoader from 'components/Loaders/SpinnerImportLoader'
import {
  getHotelMoreLocationInfoOverlay,
  getLocationVisibility
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {loadModule} from 'utils'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {StarRating} from '@vio/atlas/StarRating'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Location} from '@vio/shared/src/search/Location'
import {ReviewRating} from '@vio/shared/src/search/ReviewRating'

import {AccommodationContentOverlay} from '../../AccommodationContentOverlay'
import {BottomCTA} from '../../Offers/BottomCTA'

import type {LocationMapRendererProps} from 'components/Accommodation/LocationOverlay/LocationMapRenderer/LocationMapRenderer'

interface PropsType {
  hotel: ContentHotel | undefined
  hasOffer: boolean
  isLoading: boolean
}

const LocationMapRenderer = loadModule<LocationMapRendererProps>(
  async () =>
    import(
      /* webpackChunkName: "LocationMapRenderer" */
      'components/Accommodation/LocationOverlay/LocationMapRenderer/LocationMapRenderer'
    ),
  {loading: SpinnerImportLoader}
)

export const LocationOverlayDesktop = ({
  hotel,
  isLoading,
  hasOffer
}: PropsType) => {
  const locationInfoOverlayIsActive = useSelector(
    getHotelMoreLocationInfoOverlay
  )
  const shouldShowHotelCardClickersContent =
    useShouldShowHotelCardClickersContent()
  const locationOverlayIsActive = useSelector(getLocationVisibility)
  const {isDesktopXs, isDesktopSm} = useDeviceLayout()
  const dispatch = useDispatch()
  const handleHideOverlay = () => dispatch(hideOverlay())
  const isSmallerDesktop = isDesktopXs || isDesktopSm
  const {poi: attractions} = hotel?.location || {}

  return (
    <AccommodationContentOverlay
      isVisible={locationInfoOverlayIsActive || locationOverlayIsActive}
      onClose={handleHideOverlay}
      isSidePanelVisible={!isSmallerDesktop}
      sidePanelContent={
        <Box
          overflow="auto"
          paddingTop="s400"
          paddingLeft="s500"
          paddingRight="s500"
        >
          <StarRating
            size="xs"
            data-id="StarRating"
            rating={hotel.starRating}
            isInline
          />
          <Text variant="titleL">{hotel.hotelName}</Text>
          <Box marginTop="s300">
            <ReviewRating
              rating={hotel.guestRating.overall}
              totalRatingCount={hotel.reviewCount}
              dataId="ReviewRatingSummary"
              hotelId={hotel.objectID}
            />
          </Box>
          <Box
            paddingTop="s500"
            display="flex"
            flexDirection="column"
            gap="s200"
          >
            <Location place={hotel.placeDisplayName} hotelId={hotel.objectID} />
          </Box>
          <Box paddingTop="s600">
            <Divider />
          </Box>

          <Box paddingTop="s600" paddingBottom="s800">
            <HotelLocationContent
              variant="overlay"
              descriptions={hotel?.descriptions}
              attractions={attractions}
            />
          </Box>
        </Box>
      }
      mainContent={
        <MainContent
          hotel={hotel}
          locationOverlayIsActive={locationOverlayIsActive}
          locationInfoOverlayIsActive={locationInfoOverlayIsActive}
          isSmallerDesktop={isSmallerDesktop}
        />
      }
      headerCenterContent={
        <Text variant="titleM">
          {locationInfoOverlayIsActive && isSmallerDesktop ? (
            <FormattedMessageWrapper
              id="location.info"
              defaultMessage="Location info"
            />
          ) : (
            <FormattedMessageWrapper id="map" defaultMessage="Map" />
          )}
        </Text>
      }
      primaryRoomOffer={
        (hasOffer || isLoading) &&
        !isSmallerDesktop &&
        (shouldShowHotelCardClickersContent ? (
          <DesktopAPClickerCTA
            hotelId={hotel.objectID}
            component="DesktopLocationOverlay"
            sourceComponent="DesktopHotelLocationStaticOfferCTA"
            isLoading={isLoading}
          />
        ) : (
          <Box position="sticky" bottom="0">
            <PrimaryRoomOffer
              hotelId={hotel.objectID}
              component="DesktopLocationOverlay"
            />
          </Box>
        ))
      }
      bottomCTA={
        hasOffer &&
        isSmallerDesktop && (
          <BottomCTA
            hotelId={hotel?.objectID}
            sourceComponent="MobileHotelLocationStaticOfferCTA"
          />
        )
      }
    />
  )
}

const MainContent = ({
  hotel,
  locationOverlayIsActive,
  locationInfoOverlayIsActive,
  isSmallerDesktop
}: {
  hotel?: ContentHotel
  locationOverlayIsActive: boolean
  locationInfoOverlayIsActive: boolean
  isSmallerDesktop: boolean
}) => {
  if (!hotel) return null

  const {poi: attractions} = hotel?.location || {}

  if (locationOverlayIsActive || !isSmallerDesktop) {
    return (
      <Box width="100%" height="100%" position="relative">
        <LocationMapRenderer lat={hotel._geoloc.lat} lon={hotel._geoloc.lon} />
      </Box>
    )
  }

  if (locationInfoOverlayIsActive) {
    return (
      <Box
        paddingTop="s400"
        paddingLeft="s500"
        paddingRight="s500"
        paddingBottom="s800"
        height="100%"
        overflow="auto"
      >
        <HotelLocationContent
          variant="overlay"
          descriptions={hotel?.descriptions}
          attractions={attractions}
        />
      </Box>
    )
  }
}
